import init from '../../../js/init';
import { kebabToCamel } from '../../../js/utils';

const name = 'filtered-view';
const selector = '.filtered-view';
const gridSelector = `${selector}__grid`;
const cellSelector = '.section__items > *';
const filtersSelector = `${selector}__filters`;
const sortSelector = `${selector}__sort`;
const showmoreSelector = `${selector}__show-more`;
const filterSelector = `${selector}__filter`;
const itemHiddenClass = `${selector}__item--hidden`.substring(1);

class FilteredView {
  constructor(root) {
    const grid = root.querySelector(gridSelector);
    const filters = root.querySelector(filtersSelector);
    const filterElems = [...filters.querySelectorAll(filterSelector)].map(elem =>
      elem.querySelector('[name]')
    );
    const sortContainer = root.querySelector(sortSelector)
    const sortElem = sortContainer ? sortContainer.querySelector('[name]') : undefined;
    const showMoreContainer = root.querySelector(showmoreSelector);
    const showMore = showMoreContainer ? showMoreContainer.querySelector('button') : undefined;
    const pageSize = showMoreContainer ? parseInt(showMoreContainer.getAttribute('data-show-more')) : 0;
    const cells = [...grid.querySelectorAll(cellSelector)];

    this.grid = grid;
    this.filterValues = {};
    this.sortValue = sortElem ? sortElem.querySelector('option').value : undefined;
    this.positions = {};
    this.items = {};
    this.cells = cells;
    this.filters = filters;
    this.filterElems = filterElems;
    this.sortElem = sortElem;
    this.showMoreButton = showMore;
    this.timer = null;
    this.pageSize = pageSize;
    this.showAll = false;

    setTimeout(() => {
      this.initItems();
      this.listenToFilterChanges();
      this.listenToSortChanges();
      this.listenToShowMore();
      this.updateFilterValues();
      this.setItemPositions();
      this.listenToResize();
    }, 100);
  }

  getElementData(elem) {
    const data = {};
    [...elem.attributes].forEach(({ name: attrName, value }) => {
      if (attrName.startsWith('data-')) data[kebabToCamel(attrName.replace('data-', ''))] = value;
    });

    return data;
  }

  initItems() {
    this.items = this.cells.map(cell => ({
      elem: cell,
      data: this.getElementData(cell.firstElementChild),
    }));
    this.getPositions();
    this.filterItems();
    this.sortItems();
    this.setItemPositions();
  }

  getPosition(item) {
    return {
      left: item.offsetLeft,
      top: item.offsetTop,
      width: item.offsetWidth,
      height: item.offsetHeight,
    };
  }

  getPositions() {
    const styles = this.items.map(item => item.elem.style);
    this.items.forEach(item => (item.elem.style = {}));
    this.positions = this.items.map(item => this.getPosition(item.elem));
    this.items.forEach((item, idx) => (item.elem.style = styles[idx]));
    let count = {};
    this.positions.forEach((position, idx) => {
      count[position.top] = count[position.top] ? count[position.top] + 1 : 1;
    });
    this.items_per_row = Math.max(...Object.values(count));
  }

  setItemPositions() {
    const visible = this.items.filter(item => !item.hidden);

    visible.forEach((item, idx) => {
      item.elem.style.position = 'absolute';
      item.elem.style.left = `${this.positions[idx].left}px`;
      item.elem.style.width = `${this.positions[idx].width}px`;
      let original_height = item.elem.style.height ? parseInt(item.elem.style.height.replace('px', '')) : item.elem.offsetHeight;
      let items_to_compare = idx % this.items_per_row;
      for (let i = 1; i <= items_to_compare; i++) {
        let prev_item_height = parseInt(visible[idx - i].elem.style.height.replace('px', ''));
        if (prev_item_height > original_height) {
          original_height = prev_item_height;
        } else {
          visible[idx - i].elem.style.height = `${original_height}px`;
        }
      }
      item.elem.style.height = `${original_height}px`;
      if (idx >= this.items_per_row) {
        let prev_row_height = parseInt(visible[idx - this.items_per_row].elem.style.height.replace('px', '')) + parseInt(visible[idx - this.items_per_row].elem.style.top.replace('px', ''));
        item.elem.style.top = `${prev_row_height + 20}px`;
      } else {
        item.elem.style.top = `0px`;
      }

    });

    const tops = visible.map((item, idx) => parseInt(item.elem.style.top.replace('px', '')));
    const maxTop = Math.max.apply(null, tops);
    const heights = visible.map((item, idx) => parseInt(item.elem.style.height.replace('px', '')));
    const maxHeight = Math.max.apply(null, heights);
    this.grid.style.height = `${maxTop + maxHeight + 20}px`;
  }

  filterItems() {
    let shownCount = 0;
    this.items.forEach(item => {
      const { elem, data } = item;
      let hidden = false;

      for (const key in this.filterValues) {
        if (Object.hasOwn(this.filterValues, key)) {
          const filterValue = this.filterValues[key].trim().toLowerCase();
          const itemValue = data[key].trim().toLowerCase();
          if (filterValue && itemValue && !itemValue.includes(filterValue)) {
            hidden = true;
            break;
          }
        }
      }


      item.hidden = hidden || (this.pageSize > 0 ? (!this.showAll && shownCount >= this.pageSize) : false);
      if (item.hidden) {
        elem.classList.add(itemHiddenClass);
      } else {
        elem.classList.remove(itemHiddenClass);
        shownCount = shownCount + 1;
      }
    });
  }

  sortItems() {
    if (this.sortValue) {
      const [field, direction] = this.sortValue.split(':');
      this.items = this.items.sort((a, b) => {
        const aValue = a.data[field];
        const bValue = b.data[field];
  
        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
  }

  updateFilterValues() {
    this.filterElems.forEach(filter => {
      if (filter.type === 'radio') {
        if (filter.checked) {
          this.filterValues[filter.name] = filter.value;
        }
      } else {
        this.filterValues[filter.name] = filter.value;
      }
    });
  }

  updateSortValue() {
    if (this.sortElem) {
      this.sortValue = this.sortElem.value;
    }
  }

  listenToFilterChanges() {
    this.filters.addEventListener('input', () => {
      this.updateFilterValues();
      this.filterItems();
      this.sortItems();
      this.setItemPositions();
    });
  }

  listenToSortChanges() {
    this.filters.addEventListener('input', () => {
      this.updateSortValue();
      this.filterItems();
      this.sortItems();
      this.setItemPositions();
    });
  }

  listenToShowMore() {
    if (!this.showMoreButton) return;
    this.showMoreButton.addEventListener('click', () => {
      this.showAll = !this.showAll;
      this.showMoreButton.textContent = this.showAll ? 'Show less' : 'Show more';
      this.filterItems();
      this.sortItems();
      this.setItemPositions();
    });
  }

  listenToResize() {
    window.addEventListener('resize', () => {
      this.initItems();
    });

    const imgs = [...this.grid.querySelectorAll(`${cellSelector} img`)];
    imgs.forEach(img => {
      img.addEventListener('load', () => this.initItems());
    });
  }
}

init(Drupal, name, selector, root => {
  new FilteredView(root);
});
